/// <reference types="vite/client" />

import React, { useEffect, useRef, useState } from "react";
import styles from "./reactTagsInput.module.css";

interface ReactTagsInputProps {
  tags: string[];
  options?: string[];
  onChange: (tags: string[]) => void;
  limit?: number;
  placeholder?: string;
  containerStyle?: React.CSSProperties;
  className?: string;
  filterData?: (data: string) => boolean;
  onError?: (error: string) => void;
}

const FONT_FAMILY =
  "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif";

export default function ReactAutocompleteTagbox(props: ReactTagsInputProps) {
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const tags = props.tags || [];
  const options = props.options || [];
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (
      import.meta.env.MODE !== "production" &&
      props.options &&
      props.filterData
    ) {
      const invalidOptions = props.options.filter(
        (opt) => !props.filterData!(opt)
      );
      if (invalidOptions.length) {
        console.error(
          "[ReactTagsInput] Some options do not pass filterData and will not be selectable:",
          invalidOptions
        );
      }
    }
  }, [props.options, props.filterData]);

  const suggestions = options.length
    ? options.filter(
        (o) =>
          o.toLowerCase().includes(input.toLowerCase()) && !tags.includes(o)
      )
    : [];

  // Helper to check if a tag is valid
  const isValidTag = (tag: string): true | string => {
    if (!tag) return "The tag cannot be empty.";
    if (props.filterData && !props.filterData(tag))
      return "Some tag(s) did not pass the filter (not a valid URL).";
    if (options.length > 0 && !options.includes(tag))
      return "The tag is not in the options list.";
    if (tags.includes(tag)) return "The tag is already added.";
    if (props.limit && tags.length >= props.limit)
      return "The tag limit has been reached.";
    return true;
  };

  // Add a single tag
  const addTag = (tag: string) => {
    const valid = isValidTag(tag);
    if (valid === true) {
      props.onChange([...tags, tag]);
      setInput("");
      setHighlight(0);
      props.onError?.("");
    } else {
      props.onError?.(valid);
      setInput("");
      setHighlight(0);
    }
  };

  // Add multiple tags (used for paste or when typing multiple at once)
  const addMultipleTags = (inputString: string) => {
    const candidates = inputString
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    console.log("Candidates:", candidates);
    const newTags: string[] = [];
    let remaining = props.limit ? props.limit - tags.length : Infinity;
    let firstError: string | null = null;

    for (const tag of candidates) {
      const valid = isValidTag(tag);
      if (valid === true && !newTags.includes(tag) && remaining > 0) {
        newTags.push(tag);
        remaining--;
      } else if (valid !== true && !firstError) {
        firstError = valid as string;
      }
      if (remaining === 0) break;
    }

    // If not all candidates were processed due to limit, show limit error
    if (newTags.length) {
      props.onChange([...tags, ...newTags]);
      setInput("");
      setHighlight(0);
      // If some tags were added but some were invalid, report that
      if (candidates.length > newTags.length && firstError) {
        props.onError?.(firstError + " Some valid URLs were added.");
      } else {
        props.onError?.("");
      }
    } else if (firstError) {
      props.onError?.(firstError);
      setInput("");
      setHighlight(0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      options.length > 0 &&
      ["ArrowDown", "ArrowUp"].includes(e.key) &&
      suggestions.length
    ) {
      e.preventDefault();
      setHighlight((h) =>
        e.key === "ArrowDown"
          ? (h + 1) % suggestions.length
          : (h - 1 + suggestions.length) % suggestions.length
      );
    } else if (options.length > 0 && e.key === "Enter" && suggestions.length) {
      e.preventDefault();
      addTag(suggestions[highlight]);
    } else if (
      (e.key === "Enter" || e.key === "," || e.key === " ") &&
      input.trim()
    ) {
      e.preventDefault();
      if (isValidTag(input.trim())) {
        addTag(input.trim());
      } else {
        // If tag is invalid or already present, just clear the input
        setInput("");
        setHighlight(0);
      }
    } else if (
      (e.key === "Backspace" || e.key === "Delete") &&
      !input &&
      tags.length
    ) {
      props.onChange(tags.slice(0, -1));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    addMultipleTags(pasted);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(() => e.target.value);
    setHighlight(0);
  };

  const handleOptionClick = (option: string) => addTag(option);

  const showPlaceholder = !tags.length && !input && !focused;

  return (
    <div style={{ fontFamily: FONT_FAMILY, width: "100%" }}>
      <div
        className={[styles.tagsContainer, props.className]
          .filter(Boolean)
          .join(" ")}
        style={props.containerStyle}
        tabIndex={-1}
        onClick={() => inputRef.current?.focus()}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        {tags.map((tag, idx) => (
          <span className={styles.badge} key={tag + idx}>
            <span className={styles.badgeText}>{tag}</span>
            <button
              className={styles.removeBtn}
              tabIndex={-1}
              type="button"
              aria-label={`Remove ${tag}`}
              onClick={(e) => {
                e.stopPropagation();
                props.onChange(tags.filter((_, i) => i !== idx));
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className={styles.inputTag}
          style={{ fontFamily: FONT_FAMILY }}
          autoComplete="off"
          spellCheck={false}
        />
        {showPlaceholder && (
          <span className={styles.placeholder}>
            {props.placeholder || "Type and press ENTER to add tags..."}
          </span>
        )}
        {options.length > 0 && focused && input && suggestions.length > 0 && (
          <ul className={styles.suggestions}>
            {suggestions.map((option, idx) => (
              <li
                key={option}
                className={
                  idx === highlight
                    ? styles.suggestionActive
                    : styles.suggestion
                }
                onMouseDown={() => handleOptionClick(option)}
                onMouseEnter={() => setHighlight(idx)}
              >
                {option}
              </li>
            ))}
          </ul>
        )}
      </div>
      {props.limit && (
        <span
          className={`${styles.limitWarning} ${
            tags.length === props.limit ? styles.limitReached : ""
          }`}
        >
          {`Limit used: ${tags.length}/${props.limit}`}
        </span>
      )}
    </div>
  );
}
